import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Brand,
  CityList,
  CityRow,
  EmptyState,
  HeadingRow,
  ManualAction,
  MapCanvas,
  MapPanel,
  MapToolbar,
  Nav,
  NavInner,
  NavMeta,
  Page,
  SearchBox,
  SearchInput,
  SectionTitle,
  Shell,
  SidePanel,
  Stat,
  StatsGrid,
  SuggestionItem,
  SuggestionList,
  TextButton,
  TitleBlock,
  VisitedList,
  Workspace,
} from "./style";

const echarts = require("echarts");
const chinaGeoJson = require("china-geojson");

type CityFeature = {
  type: "Feature";
  properties: {
    name: string;
    cp?: [number, number];
  };
  geometry: unknown;
};

type City = {
  name: string;
  province: string;
  coordinate?: [number, number];
  manual?: boolean;
};

type StoredCity = City & {
  visitedAt: string;
};

const STORAGE_KEY = "china-city-map.visited";
const DIRECT_CITIES = ["北京", "上海", "天津", "重庆", "香港", "澳门", "台湾"];

const normalizeCityName = (value: string) =>
  value
    .trim()
    .replace(/\s/g, "")
    .replace(/市$/, "")
    .replace(/特别行政区$/, "")
    .replace(/自治区$/, "")
    .replace(/省$/, "");

const uniqByName = (cities: City[]) => {
  const seen = new Set<string>();
  return cities.filter((city) => {
    if (seen.has(city.name)) return false;
    seen.add(city.name);
    return true;
  });
};

const provinceFeatureMap = new Map<string, CityFeature>(
  chinaGeoJson.China.features.map((feature: CityFeature) => [
    feature.properties.name,
    feature,
  ])
);

const cityGeoJson = {
  type: "FeatureCollection",
  features: Object.keys(chinaGeoJson).reduce((features: CityFeature[], key) => {
    if (key === "China") return features;
    if (DIRECT_CITIES.includes(key)) {
      const directFeature = provinceFeatureMap.get(key);
      return directFeature ? [...features, directFeature] : features;
    }
    return [...features, ...chinaGeoJson[key].features];
  }, []),
};

const allCities = uniqByName(
  Object.keys(chinaGeoJson).reduce((cities: City[], key) => {
    if (key === "China") return cities;
    if (DIRECT_CITIES.includes(key)) {
      const directFeature = provinceFeatureMap.get(key);
      if (!directFeature) return cities;
      return [
        ...cities,
        {
          name: key,
          province: key,
          coordinate: directFeature.properties.cp,
        },
      ];
    }
    return [
      ...cities,
      ...chinaGeoJson[key].features.map((feature: CityFeature) => ({
        name: feature.properties.name,
        province: key,
        coordinate: feature.properties.cp,
      })),
    ];
  }, [])
).sort((a, b) => a.name.localeCompare(b.name, "zh-Hans-CN"));

const getStoredCities = (): StoredCity[] => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const formatDate = (isoDate: string) =>
  new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(isoDate));

const ChinaCityMap = () => {
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<any>(null);
  const [query, setQuery] = useState("");
  const [visitedCities, setVisitedCities] = useState<StoredCity[]>(getStoredCities);

  const cityByName = useMemo(() => {
    return new Map(allCities.map((city) => [city.name, city]));
  }, []);

  const visitedNameSet = useMemo(() => {
    return new Set(visitedCities.map((city) => city.name));
  }, [visitedCities]);

  const matchedSuggestions = useMemo(() => {
    const normalizedQuery = normalizeCityName(query);
    if (!normalizedQuery) return allCities.slice(0, 10);
    return allCities
      .filter((city) => {
        const normalizedName = normalizeCityName(city.name);
        const normalizedProvince = normalizeCityName(city.province);
        return (
          normalizedName.includes(normalizedQuery) ||
          normalizedProvince.includes(normalizedQuery)
        );
      })
      .slice(0, 16);
  }, [query]);

  const visitedProvinceCount = useMemo(() => {
    return new Set(visitedCities.map((city) => city.province)).size;
  }, [visitedCities]);

  const addCity = (city: City) => {
    setVisitedCities((current) => {
      if (current.some((item) => item.name === city.name)) return current;
      return [
        {
          ...city,
          visitedAt: new Date().toISOString(),
        },
        ...current,
      ];
    });
    setQuery("");
  };

  const addManualCity = () => {
    const name = query.trim();
    if (!name) return;
    const existingCity =
      cityByName.get(name) ||
      allCities.find((city) => normalizeCityName(city.name) === normalizeCityName(name));
    addCity(
      existingCity || {
        name,
        province: "自定义",
        manual: true,
      }
    );
  };

  const removeCity = (name: string) => {
    setVisitedCities((current) => current.filter((city) => city.name !== name));
  };

  const resetVisited = () => {
    setVisitedCities([]);
  };

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(visitedCities));
  }, [visitedCities]);

  useEffect(() => {
    if (!mapElementRef.current) return;

    echarts.registerMap("china-city-map", cityGeoJson);
    const chart = echarts.init(mapElementRef.current, undefined, {
      renderer: "svg",
    });
    chartRef.current = chart;

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!chartRef.current) return;

    chartRef.current.off("click");
    chartRef.current.on("click", (params: { name?: string }) => {
      if (!params.name) return;
      const city = cityByName.get(params.name);
      if (city) addCity(city);
    });
  }, [cityByName]);

  useEffect(() => {
    if (!chartRef.current) return;

    const visitedScatter = visitedCities
      .filter((city) => city.coordinate)
      .map((city) => ({
        name: city.name,
        coord: city.coordinate,
      }));

    chartRef.current.setOption({
      tooltip: {
        trigger: "item",
        formatter: (params: { name: string }) => {
          const city = cityByName.get(params.name);
          const visited = visitedNameSet.has(params.name);
          if (!city) return params.name;
          return `${city.name}<br/>${city.province}${visited ? "<br/>已点亮" : ""}`;
        },
      },
      series: [
        {
          type: "map",
          map: "china-city-map",
          roam: true,
          zoom: 1.1,
          top: 14,
          bottom: 14,
          left: 12,
          right: 12,
          layoutCenter: ["50%", "50%"],
          layoutSize: "96%",
          nameProperty: "name",
          selectedMode: false,
          scaleLimit: {
            min: 0.9,
            max: 8,
          },
          itemStyle: {
            areaColor: "#e8edf3",
            borderColor: "#ffffff",
            borderWidth: 0.85,
          },
          emphasis: {
            label: {
              show: true,
              color: "#111827",
              fontFamily: "Source Sans 3",
              fontSize: 13,
            },
            itemStyle: {
              areaColor: "#d6ecff",
              borderColor: "#0076df",
            },
          },
          label: {
            show: false,
          },
          data: allCities.map((city) => {
            const visited = visitedNameSet.has(city.name);
            return {
              name: city.name,
              value: visited ? 1 : 0,
              itemStyle: visited
                ? {
                    areaColor: "#0076df",
                    borderColor: "#ffffff",
                    borderWidth: 0.9,
                  }
                : undefined,
            };
          }),
          markPoint: {
            symbol: "circle",
            symbolSize: 9,
            label: {
              show: true,
              formatter: "{b}",
              position: "right",
              color: "#1c1c1d",
              fontSize: 12,
              fontFamily: "Source Sans 3",
            },
            itemStyle: {
              color: "#ff3636",
              borderColor: "#ffffff",
              borderWidth: 1,
            },
            data: visitedScatter,
          },
        },
      ],
    });
  }, [cityByName, visitedCities, visitedNameSet]);

  const completionRate = ((visitedCities.length / allCities.length) * 100).toFixed(1);

  return (
    <Page>
      <Nav>
        <NavInner>
          <Brand>China City Map</Brand>
          <NavMeta>
            <span>cities</span>
            <span>visited</span>
            <span>stats</span>
          </NavMeta>
        </NavInner>
      </Nav>

      <Shell>
        <HeadingRow>
          <TitleBlock>
            <h1>中国城市足迹</h1>
            <p>Personal city atlas across China.</p>
          </TitleBlock>

          <StatsGrid>
            <Stat>
              <strong>{visitedCities.length}</strong>
              <span>已点亮城市</span>
            </Stat>
            <Stat>
              <strong>{visitedProvinceCount}</strong>
              <span>覆盖省级区域</span>
            </Stat>
            <Stat>
              <strong>{completionRate}%</strong>
              <span>城市覆盖率</span>
            </Stat>
          </StatsGrid>
        </HeadingRow>

        <Workspace>
          <MapPanel>
            <MapToolbar>
              <SectionTitle>地图</SectionTitle>
              <TextButton type="button" onClick={resetVisited}>
                Reset
              </TextButton>
            </MapToolbar>
            <MapCanvas ref={mapElementRef} />
          </MapPanel>

          <SidePanel>
            <SectionTitle>城市</SectionTitle>
            <SearchBox>
              <SearchInput
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") addManualCity();
                }}
                placeholder="搜索或输入城市，例如 杭州"
              />

              <SuggestionList>
                {matchedSuggestions.map((city) => (
                  <SuggestionItem key={`${city.province}-${city.name}`}>
                    <button type="button" onClick={() => addCity(city)}>
                      <strong>{city.name}</strong>
                      <span>
                        {city.province}
                        {visitedNameSet.has(city.name) ? " · 已点亮" : ""}
                      </span>
                    </button>
                  </SuggestionItem>
                ))}
              </SuggestionList>

              {query.trim() && !matchedSuggestions.length && (
                <ManualAction type="button" onClick={addManualCity}>
                  添加“{query.trim()}”
                </ManualAction>
              )}
            </SearchBox>

            <VisitedList>
              <SectionTitle>已点亮</SectionTitle>
              {visitedCities.length ? (
                <CityList>
                  {visitedCities.map((city) => (
                    <CityRow key={`${city.province}-${city.name}`}>
                      <div>
                        <strong>{city.name}</strong>
                        <span>
                          {city.province}
                          {city.manual ? " · 自定义" : ""} · {formatDate(city.visitedAt)}
                        </span>
                      </div>
                      <button type="button" onClick={() => removeCity(city.name)}>
                        Remove
                      </button>
                    </CityRow>
                  ))}
                </CityList>
              ) : (
                <EmptyState>还没有城市记录。</EmptyState>
              )}
            </VisitedList>
          </SidePanel>
        </Workspace>
      </Shell>
    </Page>
  );
};

export default ChinaCityMap;
